import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import { DataTable2 } from '../components/ui/DataTable2';
import {
    Loader2,
    Info,
    XCircle,
    ShoppingBag,
    Calendar,
    DollarSign,
    User,
    Store,
    ChevronLeft,
    Tag,
    Hash,
    MapPin,
    Package
} from 'lucide-react';
import { useToast } from '../hooks/useToast';
import { format } from 'date-fns';
import { fetchOrderDetails } from '../apiHelper';

const OrderDetails = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { showToast } = useToast();

    const ordersId = location.state?.ordersId;
    const user = location.state?.user;

    const [orderDetails, setOrderDetails] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const loadOrderDetails = async () => {
            if (!ordersId) {
                setError("No order ID provided. Please go back to a valid link.");
                setLoading(false);
                return;
            }

            setLoading(true);
            setError(null);
            try {
                // In a real app, replace this with your actual API call.
                const fetchedData = await fetchOrderDetails(ordersId);
                // console.log(fetchedData)
                setOrderDetails(fetchedData);
            } catch (err) {
                console.error(`Error loading order ${ordersId}:`, err);
                const errorMessage = err.response?.data?.detail || `Failed to load order ${ordersId}.`;
                setError(errorMessage);
                showToast(errorMessage, 'error');
            } finally {
                setLoading(false);
            }
        };

        loadOrderDetails();
    }, [ordersId, user, showToast]);

    const orderItemColumns = useMemo(() => [
        {
            key: 'catentry.productname',
            label: 'Product Name',
            searchable: (row) => row.catentry?.productname,
            render: (value, row) => (
                <div className="flex items-center gap-1">
                    <Package size={14} className="text-gray-500" />
                    <span>{row.catentry?.productname || 'N/A'}</span>
                </div>
            )
        },
        {
            key: 'quantity',
            label: 'Quantity',
            render: (quantity) => <span>{quantity}</span>
        },
        {
            key: 'price',
            label: 'Price',
            render: (price, row) => {
                const currency = row.currency || '₦';
                return `${currency} ${parseFloat(price).toFixed(2)}`;
            }
        },
        {
            key: 'fulfillmentstatus',
            label: 'Status',
            render: (status) => (
                <span className={`px-2 py-1 rounded-full text-xs font-semibold ${status === 'OUT' ? 'bg-orange-100 text-orange-800' : 'bg-green-100 text-green-800'}`}>
                    {status.toUpperCase()}
                </span>
            )
        },
        {
            key: 'released_on',
            label: 'Date',
            render: (dateString) => {
                if (!dateString) return 'N/A';
                try {
                    return format(new Date(dateString), 'MMM dd, yyyy');
                } catch {
                    return 'Invalid Date';
                }
            }
        }
    ], []);


    if (loading) {
        return (
            <div className='flex flex-col items-center justify-center min-h-[60vh] text-center p-8'>
                <Loader2 size={32} className="animate-spin text-blue-500 mb-2" />
                <p className="text-lg text-gray-600">Loading order details...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className='flex flex-col items-center justify-center min-h-[60vh] text-center p-8 text-red-600'>
                <XCircle size={32} className="mb-2" />
                <p className="text-lg mb-4">{error}</p>
                <Button onClick={() => navigate(-1)} variant="primary" className="flex items-center justify-center mt-4">
                        <ChevronLeft size={20} className="mr-2" /> Go Back
                </Button>
            </div>
        );
    }

    if (!orderDetails) {
        return (
            <div className='flex flex-col items-center justify-center min-h-[60vh] text-center p-8 text-orange-600'>
                <Info size={32} className="mb-2" />
                <p className="text-lg mb-4">No order details available.</p>
                <Button onClick={() => navigate(-1)} variant="primary" className="mt-4">
                    <ChevronLeft size={20} className="mr-2" /> Go Back
                </Button>
            </div>
        );
    }

    // A helper function to safely access nested properties and provide a default value
    const getNestedValue = (obj, path, defaultValue = 'N/A') => {
        return path.split('.').reduce((acc, part) => acc && acc[part], obj) || defaultValue;
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            return format(new Date(dateString), 'MMM dd, yyyy h:mm a');
        } catch {
            return 'Invalid Date';
        }
    };


    return (
        <div className="p-4 sm:p-6 lg:p-8 bg-gray-50 min-h-screen">
            <Card className="p-4 sm:p-6 shadow-lg rounded-lg">
                <div className="flex items-center justify-between mb-6">
                    <h2 className="text-2xl font-bold text-text-primary flex items-center gap-2">
                        <ShoppingBag size={24} className="text-blue-600" /> Order #{orderDetails.idempotency_key}
                    </h2>
                    <Button onClick={() => navigate(-1)} variant="secondary" size="sm" className="flex items-center">
                        <ChevronLeft size={20} className="mr-2" /> Back
                    </Button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                    {/* General Order Details */}
                    <div className="flex items-start gap-3 bg-white p-4 rounded-lg shadow-sm border border-gray-100">
                        <div className="p-2 rounded-full bg-blue-100 text-blue-600">
                            <Hash size={20} />
                        </div>
                        <div>
                            <p className="text-sm font-semibold text-gray-500">Reference</p>
                            <p className="text-text-primary font-mono text-sm break-words">{getNestedValue(orderDetails, 'idempotency_key')}</p>
                        </div>
                    </div>
                    <div className="flex items-start gap-3 bg-white p-4 rounded-lg shadow-sm border border-gray-100">
                        <div className="p-2 rounded-full bg-blue-100 text-blue-600">
                            <Calendar size={20} />
                        </div>
                        <div>
                            <p className="text-sm font-semibold text-gray-500">Placed On</p>
                            <p className="text-text-primary text-sm">{formatDate(getNestedValue(orderDetails, 'placed_on'))}</p>
                        </div>
                    </div>
                    <div className="flex items-start gap-3 bg-white p-4 rounded-lg shadow-sm border border-gray-100">
                        <div className="p-2 rounded-full bg-blue-100 text-blue-600">
                            <DollarSign size={20} />
                        </div>
                        <div>
                            <p className="text-sm font-semibold text-gray-500">Total Amount</p>
                            <p className="text-text-primary text-sm font-bold">{getNestedValue(orderDetails, 'currency')} {getNestedValue(orderDetails, 'totalproduct', '0.00')}</p>
                        </div>
                    </div>
                </div>

                {/* Creator and Customer Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
                        <h3 className="text-lg font-semibold text-text-primary flex items-center gap-2 mb-4">
                            <User size={20} className="text-blue-600" /> Customer Details
                        </h3>
                        <div className="text-sm text-gray-700 space-y-2">
                            <p><span className="font-semibold">Name:</span> {getNestedValue(orderDetails, 'customer.user.first_name')} {getNestedValue(orderDetails, 'customer.user.last_name')}</p>
                            <p><span className="font-semibold">Email:</span> {getNestedValue(orderDetails, 'customer.user.credential.logon_id')}</p>
                        </div>
                    </div>
                    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
                        <h3 className="text-lg font-semibold text-text-primary flex items-center gap-2 mb-4">
                            <Tag size={20} className="text-blue-600" /> Creator Details
                        </h3>
                        <div className="text-sm text-gray-700 space-y-2">
                            <p><span className="font-semibold">Name:</span> {getNestedValue(orderDetails, 'creator.user.first_name')} {getNestedValue(orderDetails, 'creator.user.last_name')}</p>
                            <p><span className="font-semibold">Email:</span> {getNestedValue(orderDetails, 'creator.user.credential.logon_id')}</p>
                        </div>
                    </div>
                </div>

                {/* Order Items Table */}
                <h3 className="text-lg font-semibold text-text-primary flex items-center gap-2 mb-4">
                    <Package size={20} className="text-blue-600" /> Order Items
                </h3>
                {orderDetails.orderitems && orderDetails.orderitems.length > 0 ? (
                    <DataTable2
                        columns={orderItemColumns}
                        data={orderDetails.orderitems}
                        emptyMessage="No items found for this order."
                        enableSearch={false}
                        enablePagination={false}
                    />
                ) : (
                    <div className="text-center py-10 text-gray-500">
                        <Info size={32} className="mx-auto mb-2 text-gray-400" />
                        <p>No order items found.</p>
                    </div>
                )}
            </Card>
        </div>
    );
};

export default OrderDetails;
